import React, { useEffect, useRef, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiGlobe, FiRefreshCw, FiX } from 'react-icons/fi';
import {
  clearCarrierSession,
  getCarrierSession,
  getCarrierSettings,
  signedInFirstName,
  setHiddenAfterLogin,
  runAndWait,
  shareCarrierSession,
  type CarrierSession,
} from '../../services/automationService';

/**
 * "Paste the URL of your logged-in carrier page" — confirms that a person who logged in by hand in the carrier
 * browser (npm run carrier-browser) really is logged in. The server checks the session before saving the address
 * (a URL carries no login of its own). Carrier Access's carrier-browser tools then work in that session.
 */
interface CarrierSessionBarProps {
  carrierId: string;
  carrierName: string;
  /** Example address shown in the empty box. */
  placeholder: string;
  /** Page "Open carrier browser" lands on — the carrier's login page when known. */
  startUrl: string;  /** True when this carrier also has a stored login, so sharing a session is a choice rather than the only way in. */
  optional?: boolean;
  /** Only runs in the visible carrier browser window (GEICO): no "run hidden after login" switch, and the window stays open. */
  carrierBrowserOnly?: boolean;
}

const CarrierSessionBar: React.FC<CarrierSessionBarProps> = ({ carrierId, carrierName, placeholder, startUrl, optional, carrierBrowserOnly }) => {
  const [url, setUrl] = useState('');
  const [session, setSession] = useState<CarrierSession | null>(null);
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [browserOpening, setBrowserOpening] = useState(false);
  // "Run hidden after login": the visible browser is for logging in and confirming the session; the work then runs hidden.
  const [hidden, setHidden] = useState(true);
  // After "Open carrier browser" the screen keeps checking until the person is logged in, so nothing has to be pasted.
  const [waiting, setWaiting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    getCarrierSession(carrierId)
      .then((saved) => { if (isMountedRef.current) setSession(saved); })
      .catch(() => {}); // Server not running — the box still works once it is.
    getCarrierSettings()
      .then((s) => {
        if (!isMountedRef.current) return;
        setHidden(carrierBrowserOnly ? false : (s.hiddenAfterLogin?.[carrierId] ?? true));
      })
      .catch(() => {});
    return () => {
      isMountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [carrierId, carrierBrowserOnly]);

  const stopWaiting = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setWaiting(false);
  };

  // Checks every few seconds, for up to 10 minutes, whether the carrier browser is now logged in; when it is, the
  // session is confirmed on its own (the server also starts keeping it alive for the day).
  const startWaitingForLogin = () => {
    stopWaiting();
    setWaiting(true);
    setProblem(null);
    const deadline = Date.now() + 10 * 60 * 1000;
    let inFlight = false;
    pollRef.current = setInterval(async () => {
      if (inFlight || !isMountedRef.current) return;
      if (Date.now() > deadline) {
        stopWaiting();
        setProblem('Still not logged in after 10 minutes. Log in in the carrier browser window, then press "Use this session".');
        return;
      }
      inFlight = true;
      try {
        const result = await shareCarrierSession(startUrl, carrierId);
        if (result.ok && result.session && isMountedRef.current) {
          setSession(result.session);
          stopWaiting();
        }
      } catch {
        // Server unreachable for a moment — keep waiting.
      }
      inFlight = false;
    }, 8000);
  };

  const handleShare = async () => {
    const pasted = url.trim();
    if (!pasted || checking) return;
    setChecking(true);
    setProblem(null);
    try {
      const result = await shareCarrierSession(pasted, carrierId);
      if (!isMountedRef.current) return;
      if (result.ok && result.session) {
        setSession(result.session);
        setUrl('');
      } else {
        setProblem(result.message);
      }
    } catch (err) {
      if (isMountedRef.current) setProblem(err instanceof Error ? err.message : 'Could not reach the automation server.');
    }
    if (isMountedRef.current) setChecking(false);
  };

  const handleClear = async () => {
    await clearCarrierSession(carrierId).catch(() => {});
    if (isMountedRef.current) setSession(null);
  };

  const handleToggleHidden = async () => {
    const next = !hidden;
    setHidden(next);
    try {
      await setHiddenAfterLogin(carrierId, next);
    } catch {
      if (isMountedRef.current) setHidden(!next); // couldn't save — put the switch back
    }
  };

  const handleOpenBrowser = async () => {
    setBrowserOpening(true);
    await runAndWait('carrier-browser-open', [startUrl]).catch(() => {});
    if (isMountedRef.current) {
      setBrowserOpening(false);
      startWaitingForLogin();
    }
  };

  return (
    <div className="ca-session">
      <label className="ca-session__label" htmlFor={`ca-session-url-${carrierId}`}>
        <FiGlobe size={13} /> {session ? `${signedInFirstName()} is logged in` : 'Carrier browser portal'}
      </label>
      <div className="ca-session__hint">
        Log in to {carrierName} in the carrier browser, then paste that page&apos;s address here so the Digital Workforce can work in your session.
        {optional && ' Optional: without it, this carrier uses its stored login.'}
      </div>
      <div className="ca-run__form">
        <input
          id={`ca-session-url-${carrierId}`}
          className="ca-run__input ca-session__input"
          placeholder={placeholder}
          value={url}
          disabled={checking}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleShare(); }}
        />
        <button type="button" className="ca-run__btn" onClick={handleShare} disabled={checking || !url.trim()}>
          {checking ? <FiRefreshCw size={13} className="spin" /> : <FiCheckCircle size={13} />}
          {checking ? 'Checking…' : 'Use this session'}
        </button>
        <button type="button" className="ca-run__btn ca-session__secondary" onClick={handleOpenBrowser} disabled={browserOpening}>
          {browserOpening ? <FiRefreshCw size={13} className="spin" /> : <FiGlobe size={13} />}
          {browserOpening ? 'Opening…' : 'Open carrier browser'}
        </button>
      </div>

      {carrierBrowserOnly ? (
        <div className="ca-session__hint">
          {carrierName} runs in the visible carrier browser window: keep that window open and signed in. It cannot run hidden.
        </div>
      ) : (
        <div className="ca-session__hidden">
          <button
            type="button"
            className={`ca-switch ${hidden ? 'ca-switch--on' : ''}`}
            role="switch"
            aria-checked={hidden}
            aria-label="Run hidden after login"
            onClick={handleToggleHidden}
          >
            <span className="ca-switch__knob" />
          </button>
          <span>
            Run hidden after login
            <span className="ca-session__hint">
              {hidden
                ? ' — the window is only for logging in; once your session is confirmed it closes by itself and the work runs in a hidden browser.'
                : ' — off: the work runs in the visible carrier browser window.'}
            </span>
          </span>
        </div>
      )}

      {waiting && (
        <div className="ca-run__status ca-run__status--progress">
          <FiRefreshCw size={12} className="spin" />
          Waiting for you to log in to {carrierName} in the carrier browser… (it will confirm on its own)
          <button type="button" className="ca-session__clear" onClick={stopWaiting} aria-label="Stop waiting">
            <FiX size={13} />
          </button>
        </div>
      )}

      {problem && (
        <div className="ca-run__status ca-run__status--attention">
          <FiAlertTriangle size={13} /> {problem}
        </div>
      )}

      {session && !problem && (
        <div className="ca-run__status ca-run__status--success ca-session__active">
          <FiCheckCircle size={13} />
          <span className="ca-session__active-text">
            {signedInFirstName()} is logged in to {carrierName}
            {session.policyNumber ? ` · policy ${session.policyNumber}` : ''}
            {' · checked '}
            {new Date(session.verifiedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            {session.keepAliveUntil &&
              ` · staying logged in until ${new Date(session.keepAliveUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
            {(session.windowClosed || session.workRunsHidden) && ' · work runs hidden with the saved session'}
            {!session.windowClosed && !session.workRunsHidden && !session.pageIsOpen && ' · that page isn’t open in the carrier browser, but the browser is logged in'}
          </span>
          <button type="button" className="ca-session__clear" onClick={handleClear} aria-label="Stop sharing this session">
            <FiX size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CarrierSessionBar;
